import { put, select, takeEvery } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { logger } from "@tecma/logs-fe";
import { applicationStateSelectors } from "store/slices/applicationState/selectors";
import { datesStateSelectors } from "store/slices/dates/selector";
import type { StartEndDates } from "types/dates";
import routesPaths from "routes/routesPaths";
import { EnvironmentState } from "store/slices/environment/slice";
import { environmentSelectors } from "store/slices/environment/selector";

import { spacesActions } from "./space.actions";
import { getSpaceFiltersByProjectId, getSpacesByProjectId } from "./space.api";
import {
  setCurrentSpace,
  setEndSpaceLoading,
  setEndSpacesLoading,
  setSpaceLoading,
  setSpaces,
  setSpaceFilters,
  setSpacesLoading,
  setSpaceFiltersLoading,
  setEndSpaceFiltersLoading,
} from "../slice/space.slice";
import {
  SpaceFilter,
  SpaceFiltersValues,
  SpaceEntity,
} from "../../../domain/space/space.types";
import SpaceService from "../../http/newspace.client";
import { SPACES_MOCK } from "./space.mock";

function* getSpacesFiltersByProjectSaga() {
  yield put(spacesActions.getSpaceFiltersByProjectIdInProgress());
  const projectID: string = yield select(applicationStateSelectors.projectID);
  const filters: SpaceFilter[] = yield getSpaceFiltersByProjectId(projectID);
  yield put(setSpaceFilters(filters));
  yield put(spacesActions.getSpaceFiltersByProjectIdDone());
}

function* getSpacesByProjectSaga(
  action: PayloadAction<{
    dates?: StartEndDates;
    filters?: SpaceFiltersValues;
  }>,
) {
  yield put(spacesActions.getSpacesByProjectInProgress());
  const projectID: string = yield select(applicationStateSelectors.projectID);
  const datesSelected: StartEndDates = yield select(
    datesStateSelectors.currentStartEndDate,
  );
  const spaces: SpaceEntity[] = yield getSpacesByProjectId({
    projectID,
    dates: action?.payload?.dates ?? datesSelected,
    filters: action?.payload?.filters,
  });
  yield put(setSpaces(spaces));
  yield put(spacesActions.getSpacesByProjectDone());
}

function* setLoadingSpacesByProjectSaga() {
  yield put(setSpacesLoading());
}

function* setLoadingSpaceFiltersByProjectSaga() {
  yield put(setSpaceFiltersLoading());
}

function* setEndLoadingSpaceFiltersByProjectSaga() {
  yield put(setEndSpaceFiltersLoading());
}

function* setEndLoadingSpacesByProjectSaga() {
  yield put(setEndSpacesLoading());
}

function* getSpaceByIdSaga(action: PayloadAction<string>) {
  yield put(spacesActions.getSpaceByIdInProgress());
  try {
    const spaceMocked = SPACES_MOCK.find(
      (space) => space.id === action.payload,
    ) as SpaceEntity;
    const environment: EnvironmentState = yield select(
      environmentSelectors.environment,
    );
    const space: SpaceEntity = yield SpaceService.getSpaceByID(
      `${environment.REACT_APP_ASSET_API_REST_URI}${environment.REACT_APP_SPACE_API_BASE_URL}`,
      action.payload,
    );
    const mergedSpace = { ...spaceMocked, ...space };
    yield put(setCurrentSpace(mergedSpace));
    yield put(spacesActions.getSpaceByIdDone());
  } catch (err) {
    yield put(spacesActions.getSpaceByIdError(err));
  }
}

function* setLoadingSpaceByIdSaga() {
  yield put(setSpaceLoading());
}

function* setEndLoadingSpaceByIdSaga() {
  yield put(setEndSpaceLoading());
}

function* setErrorGetSpaceByIdSaga(action: PayloadAction<unknown>) {
  logger.error(action.payload);
  window.location.replace(routesPaths.spaces);
  yield put(setEndSpaceLoading());
}

export function* spaceSaga() {
  yield takeEvery(
    spacesActions.getSpacesByProjectRequested,
    getSpacesByProjectSaga,
  );
  yield takeEvery(
    spacesActions.getSpaceFiltersByProjectRequested,
    getSpacesFiltersByProjectSaga,
  );
  yield takeEvery(
    spacesActions.getSpaceFiltersByProjectIdInProgress,
    setLoadingSpaceFiltersByProjectSaga,
  );
  yield takeEvery(
    spacesActions.getSpaceFiltersByProjectIdDone,
    setEndLoadingSpaceFiltersByProjectSaga,
  );
  yield takeEvery(
    spacesActions.getSpacesByProjectInProgress,
    setLoadingSpacesByProjectSaga,
  );
  yield takeEvery(
    spacesActions.getSpacesByProjectDone,
    setEndLoadingSpacesByProjectSaga,
  );

  yield takeEvery(spacesActions.getSpaceByIdRequested, getSpaceByIdSaga);
  yield takeEvery(
    spacesActions.getSpaceByIdInProgress,
    setLoadingSpaceByIdSaga,
  );
  yield takeEvery(spacesActions.getSpaceByIdDone, setEndLoadingSpaceByIdSaga);
  yield takeEvery(spacesActions.getSpaceByIdError, setErrorGetSpaceByIdSaga);
}
